import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  User, Mail, Lock, Save, Phone, MapPin, Calendar, Globe, Twitter, Linkedin, Github,
  Bell, Shield, Trash2, Download, ChevronRight, LogOut, FileText, Instagram, Youtube,
  Eye, EyeOff, CheckCircle, AlertTriangle, ArrowLeft, Pencil, Heart, Inbox, Camera,
  Briefcase, Sparkles, X as XIcon, TrendingUp, Award, Palette, Clock, Compass,
  Zap, Activity, Star, Copy, Layers, Hash, Send, Bookmark, BookmarkCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AvatarCropModal from '../components/AvatarCropModal';
import PoliciesPanel from '../components/PoliciesPanel';
import TwoFactorSetupModal from '../components/TwoFactorSetupModal';
import SavedRolesTab from '../components/SavedRolesTab';
import LoungeComingSoon from '../components/LoungeComingSoon';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GRADIENTS = {
  aurora: 'from-cyan-500/40 via-violet-500/30 to-fuchsia-500/40',
  nebula: 'from-fuchsia-500/40 via-purple-600/40 to-indigo-500/40',
  ember: 'from-amber-500/40 via-orange-500/30 to-rose-500/40',
  forest: 'from-emerald-500/40 via-teal-500/30 to-cyan-500/40',
  midnight: 'from-indigo-600/40 via-slate-700/30 to-blue-500/40',
  royal: 'from-purple-600/50 via-violet-500/30 to-fuchsia-500/40',
};

const SOCIAL_ICONS = {
  twitter: { Icon: Twitter, label: 'Twitter / X', prefix: '@', color: 'text-sky-400' },
  linkedin: { Icon: Linkedin, label: 'LinkedIn', prefix: 'in/', color: 'text-blue-400' },
  github: { Icon: Github, label: 'GitHub', prefix: '', color: 'text-slate-300' },
  instagram: { Icon: Instagram, label: 'Instagram', prefix: '@', color: 'text-pink-400' },
  youtube: { Icon: Youtube, label: 'YouTube', prefix: '@', color: 'text-red-400' },
  roblox: { Icon: Compass, label: 'Roblox', prefix: '', color: 'text-emerald-400' },
  discord: { Icon: Hash, label: 'Discord', prefix: '', color: 'text-indigo-400' },
  tiktok: { Icon: Star, label: 'TikTok', prefix: '@', color: 'text-fuchsia-400' },
};

const STATUS_STYLE = {
  new: { label: 'New', bg: 'bg-slate-500/20', text: 'text-slate-300', dot: 'bg-slate-400' },
  reviewed: { label: 'Reviewed', bg: 'bg-blue-500/20', text: 'text-blue-300', dot: 'bg-blue-400' },
  shortlisted: { label: 'Shortlisted', bg: 'bg-emerald-500/20', text: 'text-emerald-300', dot: 'bg-emerald-400' },
  hired: { label: 'Hired', bg: 'bg-fuchsia-500/25', text: 'text-fuchsia-300', dot: 'bg-fuchsia-400' },
  rejected: { label: 'Not selected', bg: 'bg-red-500/20', text: 'text-red-300', dot: 'bg-red-400' },
};

const AccountSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'overview';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [inboxItems, setInboxItems] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '', last_name: '', phone: '', bio: '', location: '', date_of_birth: '',
    pronouns: '', timezone: '', website: '', cover_gradient: 'aurora',
    socials: {}, interests: [],
  });
  const [interestInput, setInterestInput] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [emailData, setEmailData] = useState({ new_email: '', current_password: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false });
  const [emailLoading, setEmailLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState({ email_notifications: true, newsletter: true, promotions: false });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const [cropperFile, setCropperFile] = useState(null);
  const avatarInputRef = useRef(null);

  // 2FA state
  const [twoFa, setTwoFa] = useState({ enabled: false, enabled_at: null });
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [disable2FAOpen, setDisable2FAOpen] = useState(false);
  const [disable2FACode, setDisable2FACode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);

  const authHeaders = () => {
    const t = localStorage.getItem('user_token');
    return t ? { Authorization: `Bearer ${t}` } : null;
  };

  const unreadCount = useMemo(() => inboxItems.filter(i => !i.read).length, [inboxItems]);

  useEffect(() => {
    if (!localStorage.getItem('user_token')) { navigate('/login'); return; }
    (async () => {
      await Promise.all([fetchProfile(), fetchStats(), fetchApplications(), fetchInbox(), fetchBookmarks(), fetch2FA()]);
      setLoading(false);
    })();
    const iv = setInterval(() => { fetchInbox(); fetchStats(); }, 60000);
    return () => clearInterval(iv);
  }, []);

  const fetch2FA = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/user-2fa/status`, { headers: authHeaders() });
      setTwoFa(r.data);
    } catch {}
  };

  const disable2FA = async () => {
    if (disable2FACode.length !== 6) return toast.error('Enter your 6-digit code');
    setDisabling2FA(true);
    try {
      await axios.post(`${BACKEND_URL}/api/user-2fa/disable`, { code: disable2FACode }, { headers: authHeaders() });
      toast.success('Two-factor authentication disabled');
      setTwoFa({ enabled: false, enabled_at: null });
      setDisable2FAOpen(false); setDisable2FACode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') !== activeTab) {
      params.set('tab', activeTab);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/users/profile`, { headers: authHeaders() });
      setUserData(r.data);
      setProfileForm({
        first_name: r.data.first_name || '', last_name: r.data.last_name || '',
        phone: r.data.phone || '', bio: r.data.bio || '', location: r.data.location || '',
        date_of_birth: r.data.date_of_birth || '', pronouns: r.data.pronouns || '',
        timezone: r.data.timezone || '', website: r.data.website || '',
        cover_gradient: r.data.cover_gradient || 'aurora',
        socials: r.data.socials || {}, interests: r.data.interests || [],
      });
      setEmailData(p => ({ ...p, new_email: r.data.email }));
      setNotifPrefs({
        email_notifications: r.data.email_notifications !== false,
        newsletter: r.data.subscribed_to_newsletter !== false,
        promotions: !!r.data.promotions,
      });
    } catch { /* silent */ }
  };
  const fetchStats = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/users/stats`, { headers: authHeaders() }); setStats(r.data); } catch {}
  };
  const fetchApplications = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/users/my-applications`, { headers: authHeaders() }); setApplications(r.data.applications || []); } catch {}
  };
  const fetchInbox = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/users/notifications`, { headers: authHeaders() }); setInboxItems(r.data.items || []); } catch {}
  };
  const fetchBookmarks = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/users/bookmarks`, { headers: authHeaders() }); setBookmarks(r.data.items || []); } catch {}
  };
  const removeBookmark = async (articleId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/news/${articleId}/bookmark`, {}, { headers: authHeaders() });
      setBookmarks(prev => prev.filter(b => b.article_id !== articleId));
      toast.success('Removed from reading list');
    } catch { toast.error('Failed'); }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/users/profile`, profileForm, { headers: authHeaders() });
      toast.success('Profile saved');
      setEditMode(false);
      fetchProfile(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setProfileSaving(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    if (!emailData.new_email || !emailData.current_password) return toast.error('Fill in all fields');
    setEmailLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/users/change-email`, emailData, { headers: authHeaders() });
      toast.success('Email updated');
      setEmailData(p => ({ ...p, current_password: '' }));
      fetchProfile();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setEmailLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) return toast.error('Passwords do not match');
    if (passwordData.new_password.length < 6) return toast.error('Min 6 characters');
    setPasswordLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/users/change-password`,
        { current_password: passwordData.current_password, new_password: passwordData.new_password },
        { headers: authHeaders() });
      toast.success('Password updated');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPasswordLoading(false); }
  };

  const savePrefs = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/users/notification-prefs`, notifPrefs, { headers: authHeaders() });
      toast.success('Preferences saved');
    } catch { toast.error('Failed'); }
  };

  const exportData = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/users/export-data`, { headers: authHeaders() });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `starnight-${userData?.username}.json`; a.click();
      URL.revokeObjectURL(url); toast.success('Data exported');
    } catch { toast.error('Failed'); }
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return toast.error('Type DELETE to confirm');
    setDeleteLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/users/account`, {
        headers: authHeaders(), data: { current_password: deletePassword, confirmation: deleteConfirm },
      });
      localStorage.removeItem('user_token'); localStorage.removeItem('user_data');
      toast.success('Account deleted'); navigate('/');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setDeleteLoading(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('Image must be under 10MB');
    // Open the cropper — actual upload happens after crop
    setCropperFile(file);
  };

  const finishCrop = async (croppedFile) => {
    setAvatarUploading(true);
    try {
      const fd = new FormData(); fd.append('file', croppedFile);
      const r = await axios.post(`${BACKEND_URL}/api/users/avatar`, fd, { headers: authHeaders() });
      setUserData(u => ({ ...u, avatar_url: r.data.avatar_url }));
      try {
        const s = JSON.parse(localStorage.getItem('user_data') || '{}');
        localStorage.setItem('user_data', JSON.stringify({ ...s, avatar_url: r.data.avatar_url }));
      } catch {}
      toast.success('Profile picture updated'); fetchStats();
      setCropperFile(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setAvatarUploading(false); if (avatarInputRef.current) avatarInputRef.current.value = ''; }
  };

  const removeAvatar = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/users/avatar`, { headers: authHeaders() });
      setUserData(u => ({ ...u, avatar_url: null }));
      try {
        const s = JSON.parse(localStorage.getItem('user_data') || '{}'); delete s.avatar_url;
        localStorage.setItem('user_data', JSON.stringify(s));
      } catch {}
      toast.success('Removed'); fetchStats();
    } catch { toast.error('Failed'); }
  };

  const markInboxRead = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/users/notifications/${id}/read`, {}, { headers: authHeaders() });
      setInboxItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      fetchStats();
    } catch {}
  };
  const markAllInboxRead = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/users/notifications/read-all`, {}, { headers: authHeaders() });
      setInboxItems(prev => prev.map(n => ({ ...n, read: true })));
      fetchStats();
      toast.success('All read');
    } catch {}
  };
  const deleteInbox = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/users/notifications/${id}`, { headers: authHeaders() });
      setInboxItems(prev => prev.filter(n => n.id !== id));
      fetchStats();
    } catch {}
  };

  const addInterest = () => {
    const t = interestInput.trim(); if (!t) return;
    if ((profileForm.interests || []).includes(t)) return;
    if ((profileForm.interests || []).length >= 12) return toast.error('Max 12 interests');
    setProfileForm(p => ({ ...p, interests: [...(p.interests || []), t] }));
    setInterestInput('');
  };
  const removeInterest = (t) => setProfileForm(p => ({ ...p, interests: (p.interests || []).filter(x => x !== t) }));

  const logout = () => {
    localStorage.removeItem('user_token'); localStorage.removeItem('user_data');
    navigate('/'); toast.success('Logged out');
  };

  const copyUsername = () => {
    if (!userData?.username) return;
    navigator.clipboard?.writeText(userData.username);
    toast.success('Username copied');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Layers },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'applications', label: 'My Applications', icon: Briefcase, badge: applications.length },
    { id: 'saved-roles', label: 'Saved Roles', icon: Bookmark },
    { id: 'lounge', label: 'The Lounge', icon: Sparkles, badge: 'Soon' },
    { id: 'reading', label: 'Reading List', icon: Bookmark, badge: bookmarks.length },
    { id: 'inbox', label: 'Inbox', icon: Inbox, badge: unreadCount },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'preferences', label: 'Preferences', icon: Bell },
    { id: 'privacy', label: 'Data & Privacy', icon: FileText },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const gradientClass = GRADIENTS[profileForm.cover_gradient || 'aurora'] || GRADIENTS.aurora;
  const initials = `${userData?.first_name?.charAt(0) || ''}${userData?.last_name?.charAt(0) || ''}` || 'U';

  return (
    <div className="min-h-screen bg-[#05050A] pb-20 relative" data-testid="account-settings-page">
      <style>{`
        [data-testid="site-header"] { display: none !important; }
        [data-testid="alert-banners"] { display: none !important; }
      `}</style>
      {/* Ambient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#8A2BE2]/[0.08] rounded-full blur-[140px]" />
        <div className="absolute top-40 right-1/4 w-[500px] h-[500px] bg-[#00E5FF]/[0.05] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-fuchsia-500/[0.05] rounded-full blur-[110px]" />
      </div>

      {/* Cover banner */}
      <div className={`relative w-full h-56 bg-gradient-to-br ${gradientClass} overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,229,255,0.15),transparent_60%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05050A]" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.08) 0%, transparent 40%)',
        }} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-6 flex items-center justify-between">
          <Button onClick={() => navigate('/')} variant="ghost" data-testid="back-home-btn"
            className="text-white hover:text-white bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/20 rounded-xl shadow-lg">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to website
          </Button>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-black/40 backdrop-blur-md border border-white/15">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/80 text-xs font-medium">Profile Hub</span>
            </div>
            <Button onClick={logout} variant="ghost" data-testid="hub-logout-btn"
              className="text-red-200 hover:text-white bg-red-500/20 hover:bg-red-500/30 backdrop-blur-md border border-red-500/30 rounded-xl">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      {/* Hero card overlapping banner */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 -mt-24">
        <div className="bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] rounded-3xl p-6 sm:p-8 shadow-[0_20px_80px_-20px_rgba(138,43,226,0.4)]">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            {/* Avatar */}
            <div className="relative group flex-shrink-0">
              <div className="absolute -inset-2 bg-gradient-to-br from-[#00E5FF] via-[#8A2BE2] to-fuchsia-500 rounded-3xl blur-md opacity-40 group-hover:opacity-70 transition-opacity" />
              {userData?.avatar_url ? (
                <img src={`${BACKEND_URL}${userData.avatar_url}`} alt="avatar" data-testid="hub-avatar-img"
                  className="relative w-28 h-28 rounded-3xl object-cover border-4 border-[#05050A]" />
              ) : (
                <div className="relative w-28 h-28 rounded-3xl bg-gradient-to-br from-[#8A2BE2] to-[#00E5FF] flex items-center justify-center border-4 border-[#05050A]">
                  <span className="text-4xl font-bold text-white">{initials}</span>
                </div>
              )}
              <button onClick={() => avatarInputRef.current?.click()} disabled={avatarUploading}
                data-testid="hub-avatar-upload-btn"
                className="absolute -bottom-2 -right-2 w-10 h-10 bg-[#00E5FF] hover:bg-[#4dd0e1] rounded-2xl flex items-center justify-center border-4 border-[#05050A] transition-all disabled:opacity-50 shadow-lg">
                {avatarUploading ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (<Camera className="w-4 h-4 text-black" />)}
              </button>
              <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleAvatarUpload} className="hidden" data-testid="avatar-file-input" />
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-white">
                  {userData?.first_name} {userData?.last_name}
                </h1>
                {profileForm.pronouns && (
                  <span className="px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.08] text-white/50 text-[11px]">{profileForm.pronouns}</span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap text-sm text-white/40">
                <button onClick={copyUsername} data-testid="copy-username-btn" className="hover:text-white/70 inline-flex items-center gap-1.5 group">
                  @{userData?.username} <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> {userData?.email}</span>
                {profileForm.location && (
                  <><span className="w-1 h-1 rounded-full bg-white/20" />
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {profileForm.location}</span></>
                )}
              </div>
              {profileForm.bio && (
                <p className="text-white/60 text-sm mt-3 leading-relaxed max-w-2xl">{profileForm.bio}</p>
              )}
              {profileForm.interests?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {profileForm.interests.slice(0, 8).map(t => (
                    <span key={t} className="px-2.5 py-1 rounded-full bg-gradient-to-r from-[#8A2BE2]/15 to-[#00E5FF]/15 border border-white/10 text-white/70 text-[11px]">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-2 md:gap-3 md:min-w-[280px]">
              <StatMicro icon={Briefcase} value={stats?.applications?.total || 0} label="Applications" tint="cyan" />
              <StatMicro icon={Inbox} value={stats?.inbox?.unread || 0} label="Unread" tint="fuchsia" />
              <StatMicro icon={Clock} value={stats?.days_member || 0} label="Days here" tint="violet" />
            </div>
          </div>

          {/* Socials strip */}
          {Object.keys(profileForm.socials || {}).filter(k => profileForm.socials[k]).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-white/[0.06]">
              {Object.entries(profileForm.socials).filter(([_, v]) => v).map(([k, v]) => {
                const meta = SOCIAL_ICONS[k]; if (!meta) return null;
                return (
                  <div key={k} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                    <meta.Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                    <span className="text-white/60 text-xs">{meta.prefix}{v}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Tabs + content */}
        <div className="mt-6 flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-60 flex-shrink-0">
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-2 lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              {tabs.map(t => {
                const active = activeTab === t.id;
                return (
                  <button key={t.id} onClick={() => { if (t.external) { navigate(t.external); } else { setActiveTab(t.id); } }} data-testid={`tab-${t.id}`}
                    className={`flex-shrink-0 w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                      active
                        ? 'bg-gradient-to-r from-[#8A2BE2]/25 to-[#00E5FF]/15 text-white border border-[#00E5FF]/25 shadow-[inset_0_0_20px_rgba(0,229,255,0.08)]'
                        : 'text-white/40 hover:text-white hover:bg-white/[0.04] border border-transparent'
                    }`}>
                    <t.icon className={`w-4 h-4 ${active ? 'text-[#00E5FF]' : ''}`} />
                    <span className="flex-1 text-left hidden lg:inline">{t.label}</span>
                    <span className="lg:hidden">{t.label}</span>
                    {t.badge > 0 && (
                      <span data-testid={`tab-badge-${t.id}`}
                        className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#00E5FF] text-[10px] font-bold text-black flex items-center justify-center">
                        {t.badge > 9 ? '9+' : t.badge}
                      </span>
                    )}
                    {t.external && !t.badge && (
                      <ChevronRight className="w-3 h-3 opacity-40" />
                    )}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0 space-y-4">
            {activeTab === 'overview' && (
              <OverviewTab stats={stats} userData={userData} applications={applications} inboxItems={inboxItems}
                bookmarks={bookmarks}
                onGo={setActiveTab} onCompletePrompt={() => { setActiveTab('profile'); setEditMode(true); }} />
            )}

            {activeTab === 'reading' && (
              <ReadingListTab bookmarks={bookmarks} onRemove={removeBookmark} onBrowse={() => navigate('/news')} />
            )}

            {activeTab === 'profile' && (
              <ProfileTab
                userData={userData} form={profileForm} setForm={setProfileForm}
                editMode={editMode} setEditMode={setEditMode}
                onSave={saveProfile} saving={profileSaving}
                interestInput={interestInput} setInterestInput={setInterestInput}
                addInterest={addInterest} removeInterest={removeInterest}
                onRemoveAvatar={removeAvatar}
              />
            )}

            {activeTab === 'applications' && (
              <ApplicationsTab applications={applications} onRefresh={fetchApplications} onBrowse={() => navigate('/vacancies')} />
            )}

            {activeTab === 'saved-roles' && (
              <SavedRolesTab authHeaders={authHeaders} />
            )}

            {activeTab === 'lounge' && (
              <LoungeComingSoon />
            )}

            {activeTab === 'inbox' && (
              <InboxTab items={inboxItems} unread={unreadCount}
                onRefresh={fetchInbox} onMarkRead={markInboxRead} onMarkAll={markAllInboxRead} onDelete={deleteInbox} />
            )}

            {activeTab === 'security' && (
              <SecurityTab
                emailData={emailData} setEmailData={setEmailData} onEmailChange={changeEmail} emailLoading={emailLoading}
                passwordData={passwordData} setPasswordData={setPasswordData} onPasswordChange={changePassword} passwordLoading={passwordLoading}
                showPw={showPw} setShowPw={setShowPw}
                lastLogin={userData?.last_login}
                twoFa={twoFa}
                onEnable2FA={() => setShow2FASetup(true)}
                onDisable2FA={() => setDisable2FAOpen(true)}
              />
            )}

            {activeTab === 'preferences' && (
              <PreferencesTab prefs={notifPrefs} setPrefs={setNotifPrefs} onSave={savePrefs} />
            )}

            {activeTab === 'privacy' && (
              <PrivacyTab onExport={exportData} onDeleteRequest={() => setShowDeleteModal(true)} />
            )}
          </main>
        </div>
      </div>

      {/* Delete modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#0A0A12] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Delete Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
              <p className="text-red-300 text-sm font-medium">This is permanent</p>
              <p className="text-white/50 text-xs mt-1">Your profile, applications and notifications will be permanently removed.</p>
            </div>
            <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
              placeholder="Current password" data-testid="delete-password-input"
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-white text-sm placeholder-white/20" />
            <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="Type DELETE to confirm" data-testid="delete-confirm-input"
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-white text-sm placeholder-white/20 font-mono" />
            <div className="flex gap-2">
              <Button onClick={() => setShowDeleteModal(false)} variant="outline" className="flex-1 border-white/10 text-white/60 rounded-xl">Cancel</Button>
              <Button onClick={deleteAccount} disabled={deleteLoading || deleteConfirm !== 'DELETE'} data-testid="confirm-delete-btn"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl disabled:opacity-40">
                {deleteLoading ? 'Deleting…' : 'Delete forever'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Avatar cropper */}
      <AvatarCropModal
        open={!!cropperFile}
        file={cropperFile}
        uploading={avatarUploading}
        onClose={() => { setCropperFile(null); if (avatarInputRef.current) avatarInputRef.current.value = ''; }}
        onCropComplete={finishCrop}
      />

      {/* 2FA Setup Modal */}
      <TwoFactorSetupModal
        open={show2FASetup}
        onClose={() => setShow2FASetup(false)}
        onEnabled={() => { fetch2FA(); }}
      />

      {/* Disable 2FA Confirmation */}
      <Dialog open={disable2FAOpen} onOpenChange={(v) => { if (!v) { setDisable2FAOpen(false); setDisable2FACode(''); } }}>
        <DialogContent className="bg-[#0A0A14] border border-white/[0.08] text-white max-w-sm rounded-3xl" data-testid="disable-2fa-modal">
          <DialogHeader>
            <DialogTitle className="text-white text-base font-medium">Disable two-factor auth?</DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-xs mb-4">Enter a current 6-digit code from your authenticator app to confirm.</p>
          <input
            type="text" inputMode="numeric" autoFocus maxLength={6}
            value={disable2FACode}
            onChange={(e) => setDisable2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            data-testid="disable-2fa-input"
            className="w-full text-center text-2xl tracking-[0.6em] font-mono px-4 py-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-red-400/40 focus:border-red-400/30 transition-all"
            placeholder="000000"
          />
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => { setDisable2FAOpen(false); setDisable2FACode(''); }}
              className="flex-1 py-2.5 rounded-xl text-white/60 hover:text-white text-sm border border-white/[0.08] hover:bg-white/[0.04] transition-all"
            >
              Cancel
            </button>
            <Button
              onClick={disable2FA}
              disabled={disable2FACode.length !== 6 || disabling2FA}
              data-testid="confirm-disable-2fa-btn"
              className="flex-1 bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 rounded-xl disabled:opacity-30"
            >
              {disabling2FA ? 'Disabling…' : 'Disable 2FA'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════

const StatMicro = ({ icon: Icon, value, label, tint }) => {
  const tintMap = {
    cyan: 'from-[#00E5FF]/15 to-transparent border-[#00E5FF]/20 text-[#00E5FF]',
    fuchsia: 'from-fuchsia-500/15 to-transparent border-fuchsia-500/20 text-fuchsia-300',
    violet: 'from-[#8A2BE2]/20 to-transparent border-[#8A2BE2]/25 text-[#a06de3]',
  };
  return (
    <div className={`bg-gradient-to-br ${tintMap[tint]} border rounded-2xl p-3 backdrop-blur-md`}>
      <Icon className="w-3.5 h-3.5 mb-1.5 opacity-80" />
      <div className="text-white text-xl font-light leading-none">{value}</div>
      <div className="text-white/40 text-[10px] mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
};

const Card = ({ title, icon: Icon, iconColor = 'text-[#00E5FF]', children, action, testId }) => (
  <div data-testid={testId} className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl overflow-hidden">
    {(title || action) && (
      <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between gap-3">
        <h3 className="text-white font-medium flex items-center gap-2 text-sm">
          {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />} {title}
        </h3>
        {action}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

const FieldLabel = ({ children, icon: Icon }) => (
  <label className="text-white/40 text-[11px] uppercase tracking-wider font-medium block mb-1.5">
    {Icon && <Icon className="w-3 h-3 inline mr-1.5" />}{children}
  </label>
);

const Input = ({ ...props }) => (
  <input {...props}
    className={`w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00E5FF]/40 focus:ring-1 focus:ring-[#00E5FF]/20 transition-all ${props.className || ''}`} />
);

// ── OVERVIEW ──────────────────────────────────────────────
const OverviewTab = ({ stats, userData, applications, inboxItems, bookmarks = [], onGo, onCompletePrompt }) => {
  const recent = inboxItems.slice(0, 4);
  const activeApps = applications.filter(a => ['new', 'reviewed', 'shortlisted'].includes(a.status));
  const pct = stats?.profile_completeness || 0;

  return (
    <div className="space-y-4" data-testid="overview-panel">
      {/* Completeness banner */}
      {pct < 100 && (
        <div className="bg-gradient-to-r from-[#8A2BE2]/15 via-[#00E5FF]/10 to-transparent border border-[#00E5FF]/20 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-[#00E5FF]/10 rounded-full blur-3xl" />
          <div className="relative flex items-center gap-4">
            <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-[#8A2BE2] to-[#00E5FF] p-[3px]">
              <div className="w-full h-full rounded-full bg-[#05050A] flex items-center justify-center">
                <span className="text-white font-bold text-lg">{pct}%</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-base">Complete your profile</h3>
              <p className="text-white/50 text-xs mt-0.5">A richer profile means better matches on applications and shoutouts.</p>
              <div className="w-full h-1.5 bg-white/5 rounded-full mt-3 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <Button onClick={onCompletePrompt} data-testid="complete-profile-btn"
              className="bg-white text-black hover:bg-white/90 rounded-xl text-xs px-4 h-9">
              Finish up
            </Button>
          </div>
        </div>
      )}

      {/* Big stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <BigStat icon={Briefcase} value={stats?.applications?.total || 0}
          label="Applications" sub={`${activeApps.length} active`} tint="cyan"
          onClick={() => onGo('applications')} testId="stat-applications" />
        <BigStat icon={TrendingUp} value={stats?.applications?.hired || 0}
          label="Offers" sub="Roles won" tint="fuchsia" testId="stat-offers" />
        <BigStat icon={Inbox} value={stats?.inbox?.unread || 0}
          label="Unread" sub={`${stats?.inbox?.total || 0} total`} tint="violet"
          onClick={() => onGo('inbox')} testId="stat-inbox" />
        <BigStat icon={Award} value={stats?.days_member || 0}
          label="Days here" sub={stats?.member_since ? new Date(stats.member_since).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : ''} tint="amber"
          testId="stat-days" />
      </div>

      {/* Recent activity + Applications preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Recent activity" icon={Activity} iconColor="text-fuchsia-400" testId="recent-activity"
          action={<button onClick={() => onGo('inbox')} className="text-[#00E5FF] text-xs hover:underline">View all</button>}>
          {recent.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-8">No activity yet</p>
          ) : (
            <div className="space-y-2 -m-2">
              {recent.map(n => (
                <div key={n.id} className={`p-3 rounded-xl flex items-start gap-3 ${n.read ? 'bg-white/[0.02]' : 'bg-[#00E5FF]/[0.05] border border-[#00E5FF]/15'}`}>
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8A2BE2]/30 to-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-3.5 h-3.5 text-white/70" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.read ? 'text-white/60' : 'text-white'} font-medium truncate`}>{n.title}</p>
                    <p className="text-white/40 text-xs mt-0.5 line-clamp-1">{n.body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Recent applications" icon={Briefcase} testId="recent-applications"
          action={<button onClick={() => onGo('applications')} className="text-[#00E5FF] text-xs hover:underline">View all</button>}>
          {applications.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-white/40 text-sm mb-3">No applications yet</p>
              <Button onClick={() => window.location.href = '/vacancies'} data-testid="browse-vacancies-btn"
                className="bg-white/[0.05] hover:bg-white/[0.08] text-white/80 border border-white/10 rounded-xl text-xs">
                Browse vacancies <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          ) : (
            <div className="space-y-2 -m-2">
              {applications.slice(0, 4).map(a => {
                const s = STATUS_STYLE[a.status] || STATUS_STYLE.new;
                return (
                  <div key={a.id} className="p-3 rounded-xl bg-white/[0.02] flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{a.vacancy_title}</p>
                      <p className="text-white/40 text-[11px] mt-0.5">{new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${s.bg} ${s.text} inline-flex items-center gap-1.5 flex-shrink-0`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Quick actions */}
      <Card title="Quick actions" icon={Zap} iconColor="text-amber-400" testId="quick-actions">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <QuickAction icon={Pencil} label="Edit profile" onClick={() => onGo('profile')} />
          <QuickAction icon={Briefcase} label="Browse jobs" onClick={() => window.location.href = '/vacancies'} />
          <QuickAction icon={Bookmark} label="Reading list" onClick={() => onGo('reading')} />
          <QuickAction icon={Download} label="Export data" onClick={() => onGo('privacy')} />
        </div>
      </Card>

      {/* Reading list preview */}
      {bookmarks.length > 0 && (
        <Card title="Your reading list" icon={Bookmark} iconColor="text-[#00E5FF]" testId="reading-preview"
          action={<button onClick={() => onGo('reading')} className="text-[#00E5FF] text-xs hover:underline">View all ({bookmarks.length})</button>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 -m-2">
            {bookmarks.slice(0, 4).map(b => (
              <a key={b.article_id} href="/news" data-testid={`bookmark-preview-${b.article_id}`}
                className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-[#00E5FF]/25 hover:bg-white/[0.04] transition-all flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8A2BE2]/30 to-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
                  <BookmarkCheck className="w-4 h-4 text-[#00E5FF]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-medium truncate">{b.title}</p>
                  <p className="text-white/40 text-[10px] mt-0.5 truncate">
                    {b.category ? `${b.category} · ` : ''}Saved {new Date(b.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

const BigStat = ({ icon: Icon, value, label, sub, tint, onClick, testId }) => {
  const tintMap = {
    cyan: 'from-[#00E5FF]/10 border-[#00E5FF]/15 hover:border-[#00E5FF]/30 shadow-[#00E5FF]/5',
    fuchsia: 'from-fuchsia-500/10 border-fuchsia-500/15 hover:border-fuchsia-500/30 shadow-fuchsia-500/5',
    violet: 'from-[#8A2BE2]/15 border-[#8A2BE2]/20 hover:border-[#8A2BE2]/40 shadow-[#8A2BE2]/5',
    amber: 'from-amber-500/10 border-amber-500/15 hover:border-amber-500/30 shadow-amber-500/5',
  };
  const iconMap = { cyan: 'text-[#00E5FF]', fuchsia: 'text-fuchsia-400', violet: 'text-[#a06de3]', amber: 'text-amber-400' };
  return (
    <button onClick={onClick} data-testid={testId} disabled={!onClick}
      className={`text-left bg-gradient-to-br ${tintMap[tint]} to-transparent border rounded-2xl p-5 transition-all backdrop-blur-md ${onClick ? 'cursor-pointer hover:scale-[1.02]' : 'cursor-default'}`}>
      <div className="flex items-start justify-between mb-3">
        <Icon className={`w-5 h-5 ${iconMap[tint]}`} />
        {onClick && <ChevronRight className="w-4 h-4 text-white/20" />}
      </div>
      <div className="text-white text-3xl font-light leading-none">{value}</div>
      <div className="text-white/70 text-xs mt-2 font-medium">{label}</div>
      {sub && <div className="text-white/30 text-[11px] mt-0.5">{sub}</div>}
    </button>
  );
};

const QuickAction = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} data-testid={`quick-${label.toLowerCase().replace(/\s/g, '-')}`}
    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.05] hover:border-[#00E5FF]/20 transition-all">
    <Icon className="w-4 h-4 text-[#00E5FF]" />
    <span className="text-white/70 text-xs font-medium text-center">{label}</span>
  </button>
);

// ── PROFILE ─────────────────────────────────────────────
const ProfileTab = ({ userData, form, setForm, editMode, setEditMode, onSave, saving,
  interestInput, setInterestInput, addInterest, removeInterest, onRemoveAvatar }) => {
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const updateSocial = (k, v) => setForm(p => ({ ...p, socials: { ...(p.socials || {}), [k]: v } }));

  return (
    <div className="space-y-4" data-testid="profile-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-xl font-medium">Profile</h2>
          <p className="text-white/40 text-xs mt-0.5">How the world sees you on Starnight</p>
        </div>
        {!editMode ? (
          <Button onClick={() => setEditMode(true)} data-testid="edit-profile-btn"
            className="bg-white/[0.05] hover:bg-white/[0.08] text-white border border-white/10 rounded-xl">
            <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={() => setEditMode(false)} variant="outline" className="border-white/10 text-white/60 rounded-xl">Cancel</Button>
            <Button onClick={onSave} disabled={saving} data-testid="save-profile-btn"
              className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 rounded-xl">
              <Save className="w-3.5 h-3.5 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/* Basic info */}
      <Card title="Basic info" icon={User} testId="basic-info-card">
        {editMode ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><FieldLabel>First name</FieldLabel><Input value={form.first_name} onChange={e => update('first_name', e.target.value)} data-testid="first-name-input" /></div>
              <div><FieldLabel>Last name</FieldLabel><Input value={form.last_name} onChange={e => update('last_name', e.target.value)} data-testid="last-name-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><FieldLabel icon={Phone}>Phone</FieldLabel><Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+44…" /></div>
              <div><FieldLabel>Pronouns</FieldLabel><Input value={form.pronouns} onChange={e => update('pronouns', e.target.value)} placeholder="e.g. she/her" data-testid="pronouns-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><FieldLabel icon={MapPin}>Location</FieldLabel><Input value={form.location} onChange={e => update('location', e.target.value)} placeholder="City, Country" /></div>
              <div><FieldLabel icon={Clock}>Timezone</FieldLabel><Input value={form.timezone} onChange={e => update('timezone', e.target.value)} placeholder="Europe/London" data-testid="timezone-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><FieldLabel icon={Calendar}>Date of birth</FieldLabel><Input type="date" value={form.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} /></div>
              <div><FieldLabel icon={Globe}>Website</FieldLabel><Input type="url" value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://…" data-testid="website-input" /></div>
            </div>
            <div>
              <FieldLabel>Bio</FieldLabel>
              <textarea value={form.bio} onChange={e => update('bio', e.target.value)} rows={3} maxLength={500} data-testid="bio-textarea"
                placeholder="Tell people about you…"
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00E5FF]/40 focus:ring-1 focus:ring-[#00E5FF]/20 resize-none" />
              <p className="text-white/25 text-[10px] mt-1 text-right">{(form.bio || '').length}/500</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
            <InfoRow label="Full name" value={`${form.first_name} ${form.last_name}`.trim() || '-'} />
            <InfoRow label="Pronouns" value={form.pronouns || 'Not set'} />
            <InfoRow label="Phone" value={form.phone || 'Not set'} />
            <InfoRow label="Location" value={form.location || 'Not set'} />
            <InfoRow label="Timezone" value={form.timezone || 'Not set'} />
            <InfoRow label="Website" value={form.website || 'Not set'} isLink />
            <InfoRow label="Date of birth" value={form.date_of_birth ? new Date(form.date_of_birth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not set'} />
            <InfoRow label="Bio" value={form.bio || 'Not set'} full />
          </div>
        )}
      </Card>

      {/* Cover gradient */}
      {editMode && (
        <Card title="Cover banner" icon={Palette} iconColor="text-fuchsia-400" testId="cover-card">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {Object.entries(GRADIENTS).map(([k, cls]) => (
              <button key={k} onClick={() => update('cover_gradient', k)} data-testid={`gradient-${k}`}
                className={`h-16 rounded-xl bg-gradient-to-br ${cls} border-2 transition-all ${
                  form.cover_gradient === k ? 'border-white ring-2 ring-[#00E5FF]/40' : 'border-white/10 hover:border-white/30'
                }`}>
                <span className="text-white text-[10px] capitalize font-medium block mt-8 drop-shadow">{k}</span>
              </button>
            ))}
          </div>
          {userData?.avatar_url && (
            <button onClick={onRemoveAvatar} data-testid="remove-avatar-btn"
              className="mt-4 text-red-300 hover:text-red-200 text-xs inline-flex items-center gap-1.5">
              <Trash2 className="w-3 h-3" /> Remove profile picture
            </button>
          )}
        </Card>
      )}

      {/* Social links */}
      <Card title="Social links" icon={Send} iconColor="text-sky-400" testId="socials-card">
        {editMode ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(SOCIAL_ICONS).map(([k, meta]) => (
              <div key={k} className="relative">
                <meta.Icon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${meta.color}`} />
                <input type="text" value={form.socials?.[k] || ''} onChange={e => updateSocial(k, e.target.value)}
                  placeholder={`${meta.label} ${meta.prefix ? '(' + meta.prefix + 'handle)' : ''}`} data-testid={`social-${k}-input`}
                  className="w-full pl-10 pr-3 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/40" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {Object.entries(form.socials || {}).filter(([_, v]) => v).length === 0 ? (
              <p className="text-white/30 text-sm">No social links added</p>
            ) : Object.entries(form.socials).filter(([_, v]) => v).map(([k, v]) => {
              const meta = SOCIAL_ICONS[k]; if (!meta) return null;
              return (
                <div key={k} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                  <meta.Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                  <span className="text-white/70 text-xs">{meta.label}: {meta.prefix}{v}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Interests */}
      <Card title="Interests" icon={Sparkles} iconColor="text-amber-400" testId="interests-card">
        <div className="flex flex-wrap gap-2 mb-3">
          {(form.interests || []).map(t => (
            <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#8A2BE2]/20 to-[#00E5FF]/15 border border-white/10 text-white/80 text-xs">
              {t}
              {editMode && (
                <button onClick={() => removeInterest(t)} data-testid={`remove-interest-${t}`} className="text-white/50 hover:text-red-400">
                  <XIcon className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {!editMode && (form.interests || []).length === 0 && (
            <p className="text-white/30 text-sm">No interests added</p>
          )}
        </div>
        {editMode && (
          <div className="flex gap-2">
            <input type="text" value={interestInput} onChange={e => setInterestInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInterest(); } }}
              placeholder="Add an interest (e.g. Coding)" data-testid="interest-input"
              className="flex-1 px-4 py-2 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/40" />
            <Button onClick={addInterest} data-testid="add-interest-btn"
              className="bg-white/[0.05] hover:bg-white/[0.1] text-white/80 border border-white/10 rounded-xl">Add</Button>
          </div>
        )}
      </Card>
    </div>
  );
};

const InfoRow = ({ label, value, full, isLink }) => (
  <div className={full ? 'col-span-2' : ''}>
    <p className="text-white/35 text-[11px] uppercase tracking-wider mb-1">{label}</p>
    {isLink && value !== 'Not set' && value !== '-' ? (
      <a href={value} target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline text-sm break-all">{value}</a>
    ) : (
      <p className="text-white/80 text-sm break-words">{value}</p>
    )}
  </div>
);

// ── READING LIST ─────────────────────────────────────────
const ReadingListTab = ({ bookmarks, onRemove, onBrowse }) => (
  <div data-testid="reading-panel">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-white text-xl font-medium">Reading List</h2>
        <p className="text-white/40 text-xs mt-0.5">Stories you've saved to come back to</p>
      </div>
      <Button onClick={onBrowse} data-testid="reading-browse-btn"
        className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 rounded-xl">
        <Bookmark className="w-3.5 h-3.5 mr-2" /> Browse news
      </Button>
    </div>
    {bookmarks.length === 0 ? (
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-16 text-center" data-testid="no-bookmarks">
        <div className="w-16 h-16 rounded-2xl bg-white/[0.03] mx-auto mb-4 flex items-center justify-center">
          <Bookmark className="w-7 h-7 text-white/30" />
        </div>
        <p className="text-white/60 text-sm font-medium">Nothing saved yet</p>
        <p className="text-white/30 text-xs mt-1">Tap the bookmark icon on any article to save it here.</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {bookmarks.map(b => (
          <div key={b.article_id} data-testid={`saved-${b.article_id}`}
            className="group bg-white/[0.02] border border-white/[0.05] rounded-2xl overflow-hidden hover:border-[#00E5FF]/20 transition-all">
            {b.image_url && (
              <a href="/news" className="block">
                <div className="relative aspect-[16/9] overflow-hidden bg-[#0A0A12]">
                  <img src={b.image_url.startsWith('http') ? b.image_url : `${BACKEND_URL}${b.image_url}`}
                    alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  {b.category && (
                    <span className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm text-white/90 border border-white/10 text-[9px] uppercase tracking-wider">
                      {b.category}
                    </span>
                  )}
                </div>
              </a>
            )}
            <div className="p-4">
              <a href="/news" className="block">
                <h3 className="text-white text-sm font-medium hover:text-[#00E5FF] transition-colors line-clamp-2">{b.title}</h3>
              </a>
              {b.excerpt && <p className="text-white/40 text-xs mt-2 line-clamp-2 leading-relaxed">{b.excerpt}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.05]">
                <p className="text-white/30 text-[10px]">
                  Saved {new Date(b.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                <button onClick={() => onRemove(b.article_id)} data-testid={`remove-saved-${b.article_id}`}
                  className="text-white/30 hover:text-red-400 text-xs inline-flex items-center gap-1 transition-colors">
                  <XIcon className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ── APPLICATIONS ─────────────────────────────────────────
const ApplicationsTab = ({ applications, onRefresh, onBrowse }) => (
  <div data-testid="applications-panel">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-white text-xl font-medium">My Applications</h2>
        <p className="text-white/40 text-xs mt-0.5">Track every role you've applied for</p>
      </div>
      <div className="flex gap-2">
        <Button onClick={onRefresh} variant="ghost" className="text-white/50 hover:text-white rounded-xl">Refresh</Button>
        <Button onClick={onBrowse} data-testid="browse-jobs-btn"
          className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 rounded-xl">
          <Briefcase className="w-3.5 h-3.5 mr-2" /> Browse jobs
        </Button>
      </div>
    </div>
    {applications.length === 0 ? (
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-16 text-center" data-testid="no-applications">
        <div className="w-16 h-16 rounded-2xl bg-white/[0.03] mx-auto mb-4 flex items-center justify-center">
          <Briefcase className="w-7 h-7 text-white/30" />
        </div>
        <p className="text-white/60 text-sm font-medium">No applications yet</p>
        <p className="text-white/30 text-xs mt-1">Browse open roles and apply — they'll show up here.</p>
        <Button onClick={onBrowse} className="mt-4 bg-white text-black hover:bg-white/90 rounded-xl text-xs">
          View vacancies <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    ) : (
      <div className="space-y-2">
        {applications.map(a => {
          const s = STATUS_STYLE[a.status] || STATUS_STYLE.new;
          return (
            <div key={a.id} data-testid={`app-${a.id}`}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 hover:border-[#00E5FF]/20 transition-all group">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-white text-base font-medium">{a.vacancy_title}</h3>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${s.bg} ${s.text} inline-flex items-center gap-1.5`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} animate-pulse`} /> {s.label}
                    </span>
                  </div>
                  <p className="text-white/40 text-xs">
                    Applied {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {a.reviewed_at && ` · Reviewed ${new Date(a.reviewed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    {a.has_cv && ' · CV attached'}
                  </p>
                  {a.cover_letter && (
                    <p className="text-white/50 text-xs mt-2 line-clamp-2 italic">"{a.cover_letter}"</p>
                  )}
                </div>
                {a.status === 'shortlisted' && (
                  <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                    <span className="text-emerald-300 text-[11px] font-medium">You made the shortlist</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ── INBOX ───────────────────────────────────────────────
const InboxTab = ({ items, unread, onRefresh, onMarkRead, onMarkAll, onDelete }) => {
  const iconFor = (t) => ({
    welcome: { Icon: Sparkles, bg: 'from-purple-500/25 to-fuchsia-500/15', color: 'text-fuchsia-300' },
    vacancy_application_received: { Icon: Briefcase, bg: 'from-cyan-500/25 to-blue-500/15', color: 'text-cyan-300' },
    vacancy_application_status: { Icon: Award, bg: 'from-amber-500/25 to-orange-500/15', color: 'text-amber-300' },
  }[t] || { Icon: Bell, bg: 'from-slate-500/25 to-slate-500/10', color: 'text-slate-300' });

  return (
    <div data-testid="inbox-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-white text-xl font-medium flex items-center gap-2">
            Inbox
            {unread > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-[#00E5FF] text-black text-[10px] font-bold" data-testid="inbox-unread-badge">
                {unread} new
              </span>
            )}
          </h2>
          <p className="text-white/40 text-xs mt-0.5">Application updates & account notifications</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onRefresh} variant="ghost" className="text-white/50 hover:text-white rounded-xl" data-testid="inbox-refresh-btn">Refresh</Button>
          {unread > 0 && (
            <Button onClick={onMarkAll} data-testid="inbox-mark-all-read-btn"
              className="bg-white/[0.05] hover:bg-white/[0.1] text-white/80 border border-white/10 rounded-xl">
              <CheckCircle className="w-3.5 h-3.5 mr-2" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-16 text-center" data-testid="inbox-empty">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.03] mx-auto mb-4 flex items-center justify-center">
            <Inbox className="w-7 h-7 text-white/30" />
          </div>
          <p className="text-white/60 text-sm font-medium">You're all caught up</p>
          <p className="text-white/30 text-xs mt-1">Notifications about applications and account updates will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(n => {
            const meta = iconFor(n.type);
            const st = n.meta?.status;
            return (
              <div key={n.id} data-testid={`inbox-item-${n.id}`}
                className={`p-4 rounded-2xl border flex gap-4 transition-all ${
                  n.read ? 'bg-white/[0.02] border-white/[0.05]' :
                  'bg-gradient-to-r from-[#00E5FF]/[0.05] to-[#8A2BE2]/[0.05] border-[#00E5FF]/20 shadow-[0_0_20px_rgba(0,229,255,0.05)]'
                }`}>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                  <meta.Icon className={`w-5 h-5 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-semibold ${n.read ? 'text-white/70' : 'text-white'}`}>{n.title}</p>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF]" />}
                        {st && (
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLE[st]?.bg} ${STATUS_STYLE[st]?.text}`}>
                            {STATUS_STYLE[st]?.label || st}
                          </span>
                        )}
                      </div>
                      <p className="text-white/50 text-xs mt-1 leading-relaxed">{n.body}</p>
                      <p className="text-white/25 text-[10px] mt-2">
                        {new Date(n.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <button onClick={() => onDelete(n.id)} data-testid={`inbox-delete-${n.id}`}
                      className="text-white/25 hover:text-red-400 p-1 rounded-lg" title="Delete">
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {!n.read && (
                    <button onClick={() => onMarkRead(n.id)} data-testid={`inbox-mark-read-${n.id}`}
                      className="text-[#00E5FF] hover:text-[#00E5FF]/80 text-[11px] font-medium mt-2 inline-flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Mark as read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── SECURITY ────────────────────────────────────────────
const SecurityTab = ({ emailData, setEmailData, onEmailChange, emailLoading,
  passwordData, setPasswordData, onPasswordChange, passwordLoading, showPw, setShowPw, lastLogin,
  twoFa, onEnable2FA, onDisable2FA }) => (
  <div className="space-y-4" data-testid="security-panel">
    <div>
      <h2 className="text-white text-xl font-medium">Security</h2>
      <p className="text-white/40 text-xs mt-0.5">Keep your account locked down</p>
    </div>

    {/* Two-Factor Authentication */}
    <div
      className={`rounded-2xl overflow-hidden border transition-all ${
        twoFa?.enabled
          ? 'border-emerald-500/25 bg-emerald-500/[0.04]'
          : 'border-amber-500/25 bg-amber-500/[0.04]'
      }`}
      data-testid="two-factor-card"
    >
      <div className="p-5 flex items-start gap-4">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border ${
            twoFa?.enabled
              ? 'bg-emerald-500/15 border-emerald-500/30'
              : 'bg-amber-500/15 border-amber-500/30'
          }`}
        >
          <Shield className={`w-5 h-5 ${twoFa?.enabled ? 'text-emerald-300' : 'text-amber-300'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white text-sm font-medium">Two-factor authentication</h3>
            {twoFa?.enabled ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                On
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/25">
                Off
              </span>
            )}
          </div>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">
            {twoFa?.enabled
              ? "You'll be asked for a 6-digit code from your authenticator app every time you sign in."
              : 'Add an extra layer of security — the same protection our staff portal uses. Requires an authenticator app (Google Authenticator, Authy, 1Password).'}
          </p>
          {twoFa?.enabled && twoFa?.enabled_at && (
            <p className="text-white/30 text-[11px] mt-2">
              Enabled {new Date(twoFa.enabled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          <div className="mt-4">
            {twoFa?.enabled ? (
              <Button
                onClick={onDisable2FA}
                data-testid="disable-2fa-btn"
                className="bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/25 rounded-xl"
              >
                Disable 2FA
              </Button>
            ) : (
              <Button
                onClick={onEnable2FA}
                data-testid="enable-2fa-btn"
                className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 rounded-xl"
              >
                <Shield className="w-3.5 h-3.5 mr-2" /> Enable 2FA
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>

    {lastLogin && (
      <Card title="Last sign-in" icon={Clock} iconColor="text-emerald-400" testId="last-login-card">
        <p className="text-white/70 text-sm">
          {new Date(lastLogin).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
        </p>
        <p className="text-white/30 text-xs mt-1">If you don't recognise this, change your password immediately.</p>
      </Card>
    )}

    <Card title="Change email" icon={Mail} testId="email-card">
      <form onSubmit={onEmailChange} className="space-y-3">
        <div><FieldLabel>New email</FieldLabel>
          <Input type="email" value={emailData.new_email} onChange={e => setEmailData({ ...emailData, new_email: e.target.value })} data-testid="new-email-input" />
        </div>
        <div><FieldLabel>Current password</FieldLabel>
          <div className="relative">
            <Input type={showPw.current ? 'text' : 'password'} value={emailData.current_password}
              onChange={e => setEmailData({ ...emailData, current_password: e.target.value })} className="pr-10" data-testid="email-current-password-input" />
            <button type="button" onClick={() => setShowPw(p => ({ ...p, current: !p.current }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              {showPw.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={emailLoading} data-testid="save-email-btn"
          className="w-full bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30 hover:bg-[#00E5FF]/30 rounded-xl">
          {emailLoading ? 'Updating…' : 'Update email'}
        </Button>
      </form>
    </Card>

    <Card title="Change password" icon={Lock} iconColor="text-amber-400" testId="password-card">
      <form onSubmit={onPasswordChange} className="space-y-3">
        <div><FieldLabel>Current password</FieldLabel>
          <Input type="password" value={passwordData.current_password}
            onChange={e => setPasswordData({ ...passwordData, current_password: e.target.value })} data-testid="pw-current-input" />
        </div>
        <div><FieldLabel>New password</FieldLabel>
          <div className="relative">
            <Input type={showPw.next ? 'text' : 'password'} value={passwordData.new_password}
              onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })}
              placeholder="Min 6 characters" className="pr-10" data-testid="pw-new-input" />
            <button type="button" onClick={() => setShowPw(p => ({ ...p, next: !p.next }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              {showPw.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div><FieldLabel>Confirm new password</FieldLabel>
          <Input type="password" value={passwordData.confirm_password}
            onChange={e => setPasswordData({ ...passwordData, confirm_password: e.target.value })} data-testid="pw-confirm-input" />
        </div>
        <Button type="submit" disabled={passwordLoading} data-testid="save-password-btn"
          className="w-full bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 rounded-xl">
          {passwordLoading ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </Card>
  </div>
);

// ── PREFERENCES ─────────────────────────────────────────
const PreferencesTab = ({ prefs, setPrefs, onSave }) => {
  const items = [
    { key: 'email_notifications', label: 'Email notifications', desc: 'Account-related emails (application updates, alerts)', icon: Mail, color: 'cyan' },
    { key: 'newsletter', label: 'Monthly newsletter', desc: 'Big announcements and company updates', icon: Globe, color: 'emerald' },
    { key: 'promotions', label: 'Promotions & offers', desc: 'Occasional exclusive deals & partner offers', icon: Heart, color: 'pink' },
  ];
  const colorMap = { cyan: 'bg-[#00E5FF]/15 text-[#00E5FF]', emerald: 'bg-emerald-500/15 text-emerald-400', pink: 'bg-pink-500/15 text-pink-400' };
  return (
    <div className="space-y-4" data-testid="preferences-panel">
      <div>
        <h2 className="text-white text-xl font-medium">Preferences</h2>
        <p className="text-white/40 text-xs mt-0.5">Control the noise level</p>
      </div>
      <Card icon={Bell} title="Notification channels" iconColor="text-fuchsia-400">
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.key} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorMap[it.color]}`}>
                  <it.icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{it.label}</p>
                  <p className="text-white/40 text-xs">{it.desc}</p>
                </div>
              </div>
              <button onClick={() => setPrefs({ ...prefs, [it.key]: !prefs[it.key] })} data-testid={`toggle-${it.key}`}
                className={`w-12 h-7 rounded-full transition-all flex items-center ${prefs[it.key] ? 'bg-[#00E5FF] justify-end' : 'bg-white/10 justify-start'}`}>
                <div className="w-5 h-5 bg-white rounded-full mx-1 shadow" />
              </button>
            </div>
          ))}
          <Button onClick={onSave} data-testid="save-prefs-btn"
            className="w-full mt-3 bg-gradient-to-r from-[#00E5FF]/20 to-[#8A2BE2]/20 text-white border border-white/10 hover:from-[#00E5FF]/30 hover:to-[#8A2BE2]/30 rounded-xl">
            <Save className="w-3.5 h-3.5 mr-2" /> Save preferences
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ── PRIVACY ─────────────────────────────────────────────
const PrivacyTab = ({ onExport, onDeleteRequest }) => (
  <div className="space-y-5" data-testid="privacy-panel">
    <div>
      <h2 className="text-white text-xl font-medium">Data & Privacy</h2>
      <p className="text-white/40 text-xs mt-0.5">Your data, your rules — and every policy that protects it.</p>
    </div>

    <Card title="Export your data" icon={Download} iconColor="text-emerald-400">
      <p className="text-white/50 text-sm mb-4">Get a JSON snapshot of your profile and activity history — GDPR-ready.</p>
      <Button onClick={onExport} data-testid="export-data-btn"
        className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 rounded-xl">
        <Download className="w-3.5 h-3.5 mr-2" /> Export my data
      </Button>
    </Card>

    <PoliciesPanel />

    <div className="bg-red-950/30 border border-red-500/20 rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-red-500/10">
        <h3 className="text-red-300 font-medium flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" /> Danger zone
        </h3>
      </div>
      <div className="p-6">
        <p className="text-white/50 text-sm mb-4">Permanently delete your account and all associated data. This cannot be undone.</p>
        <Button onClick={onDeleteRequest} data-testid="delete-account-btn"
          className="bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 rounded-xl">
          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete my account
        </Button>
      </div>
    </div>
  </div>
);

export default AccountSettings;
